import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "~/contexts/auth-context";
import { isSalesDataLakeEnabled } from "~/shared/sales-data/sales-data-lake";
import { FilterItem, FilterType } from "./types";

export const useFilters = () => {
  const { t } = useTranslation(["books", "general"]);
  const { organisation } = useSession();
  const salesDataLake = organisation.settings?.salesDataLake;
  const showSalesDataFilter = isSalesDataLakeEnabled(salesDataLake);

  const filters = useMemo<FilterItem[]>(
    () => [
      {
        icon: "square-stack",
        label: t("books:filters.keywordCount"),
        type: FilterType.NUMERIC,
        name: "keywordCount",
        show: true,
      },
      {
        icon: "type",
        label: t("books:filters.descriptionLength"),
        type: FilterType.NUMERIC,
        name: "descriptionLength",
        show: true,
        tooltip: t("books:filters.descriptionLengthTooltip"),
      },
      {
        icon: "whole-word",
        label: t("books:filters.descriptionWordCount"),
        type: FilterType.NUMERIC,
        name: "descriptionWordCount",
        show: true,
        tooltip: t("books:filters.descriptionWordCountTooltip"),
      },
      {
        icon: "book-type",
        label: t("books:filters.themaCodes.label"),
        placeholder: t("books:filters.themaCodes.placeholder"),
        type: FilterType.CHECKBOX,
        name: "themaCodes",
        show: true,
        badgeLabel: t("books:filters.themaCodes.badgeLabel"),
        showSelectAll: true,
      },
      {
        icon: "tags",
        label: t("books:filters.themaCodeCount"),
        type: FilterType.NUMERIC,
        name: "themaCodeCount",
        show: true,
      },
      {
        icon: "library",
        label: t("books:filters.bisacCodes.label"),
        placeholder: t("books:filters.bisacCodes.placeholder"),
        type: FilterType.CHECKBOX,
        name: "bisacCodes",
        show: true,
        badgeLabel: t("books:filters.bisacCodes.badgeLabel"),
        tooltip: t("books:filters.bisacCodes.tooltip"),
      },
      {
        icon: "tags",
        label: t("books:filters.bisacCodeCount"),
        type: FilterType.NUMERIC,
        name: "bisacCodeCount",
        show: true,
      },
      {
        icon: "baby",
        label: t("books:filters.ageRange.title"),
        type: FilterType.AGE_RANGE,
        name: "ageRange",
        show: true,
      },
      {
        icon: "calendar-days",
        label: t("books:filters.publishingDate"),
        type: FilterType.DATE_RANGE,
        name: "publishedAt",
        show: true,
      },
      {
        icon: "book",
        label: t("books:filters.bookFormat.label"),
        placeholder: t("books:filters.bookFormat.placeholder"),
        type: FilterType.CHECKBOX,
        name: "productGroupDescription",
        show: true,
        badgeLabel: t("books:filters.bookFormat.badgeLabel"),
        showSelectAll: true,
      },
      {
        icon: "school",
        label: t("books:filters.publisher.label"),
        placeholder: t("books:filters.publisher.placeholder"),
        type: FilterType.CHECKBOX,
        name: "publisher",
        show: true,
        badgeLabel: t("books:filters.publisher.badgeLabel"),
        showSelectAll: true,
      },
      {
        icon: "stamp",
        label: t("books:filters.imprint.label"),
        placeholder: t("books:filters.imprint.placeholder"),
        type: FilterType.CHECKBOX,
        name: "imprints",
        show: true,
        badgeLabel: t("books:filters.imprint.badgeLabel"),
        showSelectAll: true,
      },
      {
        icon: "pen-line",
        label: t("books:filters.author.label"),
        placeholder: t("books:filters.author.placeholder"),
        type: FilterType.CHECKBOX,
        name: "author",
        show: true,
        badgeLabel: t("books:filters.author.badgeLabel"),
        showSelectAll: true,
      },
      {
        icon: "tag",
        label: t("books:filters.genre.label"),
        placeholder: t("books:filters.genre.placeholder"),
        type: FilterType.CHECKBOX,
        name: "genre",
        show: true,
        badgeLabel: t("books:filters.genre.badgeLabel"),
        showSelectAll: true,
      },
      {
        icon: "hash",
        label: t("books:filters.keywords.label"),
        placeholder: t("books:filters.keywords.placeholder"),
        type: FilterType.CHECKBOX,
        name: "keywords",
        show: true,
        badgeLabel: t("books:filters.keywords.badgeLabel"),
        showSelectAll: true,
      },
      {
        icon: "chart-column-increasing",
        label: t("books:filters.salesData.label"),
        type: FilterType.SALES_DATA,
        name: "salesData",
        show: showSalesDataFilter,
      },
    ],
    [showSalesDataFilter, t],
  );

  return { filters };
};
